function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { cityNameValidation, companyNameValidation, invoicePrefixValidation, postalCodeValidation, streetNameValidation } from 'validators';
import { KYBProgressStatusEnum, KYBProgressStatusHelpers } from 'anchor-kyb-service/types';
import { Button, Callout, Card, Checkbox, Divider, MenuItem, Page, Select, Spinner, TextField, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { makeSentryIgnoredError } from "../../components/ErrorBoundaryPage";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { LogoPicker } from "../../components/LogoPicker/LogoPicker";
import { NavigationGuardModal } from "../../components/NavigationGuardModal";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { ownershipTypes, states } from "../../constants";
import { useStore, useStoreActions } from "../../contexts";
import { useConnectBankAccount } from "../../hooks/useConnectBankAccount";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { useStartKYC } from "../../hooks/useStartKYCPersona";
import { useUploadLogo } from "../../hooks/useUploadLogo";
import { Intent } from "../../infra/access-control";
import { handleRequest } from "../../infra/utils";
import { selectors } from "../../store/state-selectors";
import { isBankAccountConnected } from "../../store/store.model";
import { helpArticles } from "../../utils/helpArticles";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import styles from "./BusinessDetails.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const formId = 'business-details-form';
const testPrefix = 'settings-business-details';
const title = 'Business details';
export const BusinessDetailsTestIds = getTestIds('BusinessDetailsTestIds');
const ownershipTypeList = ownershipTypes.map(_ref => {
  let {
    value,
    display
  } = _ref;
  return {
    value,
    label: display
  };
});
const extraPrefixMaxLength = 8;
const defaultMaxPrefixLength = 4;
export function BusinessDetails() {
  const worksAt = useStore(selectors.user.business);
  const workspaces = useStore(selectors.user.workspaces);
  if (!workspaces) throw makeSentryIgnoredError('no workspaces');
  if (!worksAt) throw makeSentryIgnoredError('no business');
  return /*#__PURE__*/_jsx(BusinessDetailsWithData, {
    worksAt: worksAt,
    workspaces: workspaces
  });
}
function BusinessDetailsWithData(_ref2) {
  var _worksAt$settings$inv, _worksAt$settings, _worksAt$settings5;
  let {
    worksAt,
    workspaces
  } = _ref2;
  const isVendor = useStore(selectors.user.isVendor);
  const payoutBankAccount = useStore(selectors.user.payoutBankAccount);
  const invoiceLineItemsGrouped = useStore(selectors.user.invoiceLineItemsGrouped);
  const {
    api,
    setWorksAt,
    logBI,
    setWorkspaces
  } = useStoreActions();
  const {
    Description,
    ConnectButton,
    BankAccountDetails
  } = useConnectBankAccount();
  const {
    startKYC
  } = useStartKYC();
  const personaKYBStatus = useStore(s => {
    var _s$personaKYBStatus$d;
    return (_s$personaKYBStatus$d = s.personaKYBStatus.data) === null || _s$personaKYBStatus$d === void 0 ? void 0 : _s$personaKYBStatus$d.status;
  });
  const [grouped, setGrouped] = useState(invoiceLineItemsGrouped);
  const {
    showSuccessToast
  } = useOverlayContext();
  const {
    uploadLogo
  } = useUploadLogo(worksAt);
  const {
    BUSINESS_DESCRIPTION_ONBOARDING
  } = useFeatureFlags();
  const maxInvoicePrefixLength = ((_worksAt$settings$inv = (_worksAt$settings = worksAt.settings) === null || _worksAt$settings === void 0 || (_worksAt$settings = _worksAt$settings.invoicePrefix) === null || _worksAt$settings === void 0 ? void 0 : _worksAt$settings.length) !== null && _worksAt$settings$inv !== void 0 ? _worksAt$settings$inv : 0) > defaultMaxPrefixLength ? extraPrefixMaxLength : defaultMaxPrefixLength;
  useSetDocumentTitle(title);
  const businessOwnership = useMemo(() => ownershipTypeList.find(_ref3 => {
    let {
      value
    } = _ref3;
    return value === worksAt.ownership;
  }) || null, [worksAt.ownership]);
  const {
    control,
    formState,
    handleSubmit,
    watch,
    reset
  } = useForm({
    mode: 'all',
    defaultValues: getInitialFormState()
  });
  const invoicePrefix = watch('invoicePrefix');
  function getInitialFormState() {
    var _worksAt$postalAddres, _worksAt$postalAddres2, _worksAt$postalAddres3, _worksAt$postalAddres4, _worksAt$postalAddres5, _worksAt$postalAddres6, _worksAt$settings2;
    return {
      businessName: worksAt.name,
      businessDescription: worksAt.description || '',
      address1: (_worksAt$postalAddres = worksAt.postalAddress) === null || _worksAt$postalAddres === void 0 ? void 0 : _worksAt$postalAddres.addressOne,
      address2: (_worksAt$postalAddres2 = (_worksAt$postalAddres3 = worksAt.postalAddress) === null || _worksAt$postalAddres3 === void 0 ? void 0 : _worksAt$postalAddres3.addressTwo) !== null && _worksAt$postalAddres2 !== void 0 ? _worksAt$postalAddres2 : '',
      city: (_worksAt$postalAddres4 = worksAt.postalAddress) === null || _worksAt$postalAddres4 === void 0 ? void 0 : _worksAt$postalAddres4.city,
      state: (_worksAt$postalAddres5 = worksAt.postalAddress) === null || _worksAt$postalAddres5 === void 0 ? void 0 : _worksAt$postalAddres5.state,
      postalCode: (_worksAt$postalAddres6 = worksAt.postalAddress) === null || _worksAt$postalAddres6 === void 0 ? void 0 : _worksAt$postalAddres6.postalCode,
      ownership: businessOwnership,
      logo: {
        asDataURL: worksAt.logoUrl,
        file: null
      },
      invoicePrefix: (_worksAt$settings2 = worksAt.settings) === null || _worksAt$settings2 === void 0 ? void 0 : _worksAt$settings2.invoicePrefix
    };
  }
  function resetFormState(updatedInvoicePrefix, updatedWorksAt) {
    var _targetWorksAt$postal, _targetWorksAt$postal2, _targetWorksAt$postal3, _targetWorksAt$postal4, _targetWorksAt$postal5, _targetWorksAt$postal6, _targetWorksAt$settin;
    const targetWorksAt = updatedWorksAt !== null && updatedWorksAt !== void 0 ? updatedWorksAt : worksAt;
    reset({
      businessName: targetWorksAt.name,
      businessDescription: targetWorksAt.description || '',
      address1: (_targetWorksAt$postal = targetWorksAt.postalAddress) === null || _targetWorksAt$postal === void 0 ? void 0 : _targetWorksAt$postal.addressOne,
      address2: (_targetWorksAt$postal2 = (_targetWorksAt$postal3 = targetWorksAt.postalAddress) === null || _targetWorksAt$postal3 === void 0 ? void 0 : _targetWorksAt$postal3.addressTwo) !== null && _targetWorksAt$postal2 !== void 0 ? _targetWorksAt$postal2 : '',
      city: (_targetWorksAt$postal4 = targetWorksAt.postalAddress) === null || _targetWorksAt$postal4 === void 0 ? void 0 : _targetWorksAt$postal4.city,
      state: (_targetWorksAt$postal5 = targetWorksAt.postalAddress) === null || _targetWorksAt$postal5 === void 0 ? void 0 : _targetWorksAt$postal5.state,
      postalCode: (_targetWorksAt$postal6 = targetWorksAt.postalAddress) === null || _targetWorksAt$postal6 === void 0 ? void 0 : _targetWorksAt$postal6.postalCode,
      ownership: ownershipTypeList.find(_ref4 => {
        let {
          value
        } = _ref4;
        return value === targetWorksAt.ownership;
      }) || null,
      logo: {
        asDataURL: targetWorksAt.logoUrl,
        file: null
      },
      invoicePrefix: updatedInvoicePrefix !== null && updatedInvoicePrefix !== void 0 ? updatedInvoicePrefix : (_targetWorksAt$settin = targetWorksAt.settings) === null || _targetWorksAt$settin === void 0 ? void 0 : _targetWorksAt$settin.invoicePrefix
    });
  }
  async function onSubmit(_ref5) {
    var _initialFormState$own;
    let {
      businessName,
      businessDescription,
      address1,
      address2,
      city,
      state,
      postalCode,
      ownership,
      logo
    } = _ref5;
    logBI(10420);
    const params = {};
    const initialFormState = getInitialFormState();
    if (businessName !== initialFormState.businessName) params.name = businessName;
    if (businessDescription !== initialFormState.businessDescription) {
      params.description = businessDescription;
    }
    if (logo !== null && logo !== void 0 && logo.file && logo !== null && logo !== void 0 && logo.asDataURL) {
      uploadLogo({
        file: logo.file,
        asDataURL: logo.asDataURL
      });
      params.logoUrl = logo.asDataURL;
    }
    if (address1 !== initialFormState.address1 || address2 !== initialFormState.address2 || city !== initialFormState.city || state !== initialFormState.state || postalCode !== initialFormState.postalCode) {
      var _worksAt$postalAddres7;
      params.postalAddress = {
        address1,
        address2: address2 || null,
        city,
        state,
        postalCode,
        country: ((_worksAt$postalAddres7 = worksAt.postalAddress) === null || _worksAt$postalAddres7 === void 0 ? void 0 : _worksAt$postalAddres7.country) || 'US'
      };
    }
    if (ownership && ownership.value !== ((_initialFormState$own = initialFormState.ownership) === null || _initialFormState$own === void 0 ? void 0 : _initialFormState$own.value)) params.ownership = ownership.value;
    if (!Object.keys(params).length) return;
    const {
      success
    } = await handleRequest(api.vendor.updateBusinessDetails(worksAt.id, params));
    if (success) {
      var _worksAt$postalAddres8, _worksAt$postalAddres9, _logo$asDataURL;
      const {
        name,
        description,
        ownership,
        postalAddress
      } = params;
      const shouldChangeWorkspace = !!name;
      const updatedBusinessData = _objectSpread(_objectSpread({}, worksAt), {}, {
        name: name !== null && name !== void 0 ? name : worksAt.name,
        description: description !== undefined ? description : worksAt.description,
        ownership: ownership !== null && ownership !== void 0 ? ownership : worksAt.ownership,
        postalAddress: postalAddress ? {
          addressOne: postalAddress.address1,
          addressTwo: postalAddress.address2,
          city: postalAddress.city,
          state: postalAddress.state,
          postalCode: postalAddress.postalCode,
          country: (_worksAt$postalAddres8 = (_worksAt$postalAddres9 = worksAt.postalAddress) === null || _worksAt$postalAddres9 === void 0 ? void 0 : _worksAt$postalAddres9.country) !== null && _worksAt$postalAddres8 !== void 0 ? _worksAt$postalAddres8 : 'US'
        } : worksAt.postalAddress,
        logoUrl: (_logo$asDataURL = logo.asDataURL) !== null && _logo$asDataURL !== void 0 ? _logo$asDataURL : worksAt.logoUrl
      });
      setWorksAt(updatedBusinessData);
      if (shouldChangeWorkspace) {
        const newWorkspaces = workspaces.map(workspace => workspace.id() === worksAt.id ? workspace.setName(name) : workspace);
        setWorkspaces(newWorkspaces);
      }
      resetFormState(undefined, updatedBusinessData);
    }
  }
  async function handleSaveBillingSettings() {
    var _worksAt$settings3, _worksAt$settings4;
    if (grouped === invoiceLineItemsGrouped && invoicePrefix === ((_worksAt$settings3 = worksAt.settings) === null || _worksAt$settings3 === void 0 ? void 0 : _worksAt$settings3.invoicePrefix) || !worksAt.settings || !!formState.errors.invoicePrefix) return;
    const invoiceLineItems = grouped ? 'grouped' : 'detailed';
    const {
      success
    } = await handleRequest(api.vendor.updateBusinessSettings(worksAt.id, _objectSpread(_objectSpread({}, worksAt.settings), {}, {
      invoiceLineItems,
      coverCreditCardFees: ((_worksAt$settings4 = worksAt.settings) === null || _worksAt$settings4 === void 0 ? void 0 : _worksAt$settings4.feeControls.coverCreditCardFees) || false,
      invoicePrefix
    })));
    if (success) {
      showSuccessToast('Billing settings saved successfully', 'billing-settings-success-update-toast');
      const updatedWorksAt = _objectSpread(_objectSpread({}, worksAt), {}, {
        settings: worksAt.settings && _objectSpread(_objectSpread({}, worksAt.settings), {}, {
          invoiceLineItems,
          invoicePrefix
        })
      });
      setWorksAt(updatedWorksAt);
      resetFormState(invoicePrefix, updatedWorksAt);
    }
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(Page, {
      header: title,
      testId: testPrefix + '-screen',
      skin: "secondary",
      children: [/*#__PURE__*/_jsxs("form", {
        id: formId,
        onSubmit: handleSubmit(onSubmit),
        children: [/*#__PURE__*/_jsxs(Card, {
          className: styles.fieldsLayout,
          autoHeight: true,
          padding: "m",
          disableSeparator: true,
          title: "Details",
          children: [/*#__PURE__*/_jsx(Divider, {
            margin: 8
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.detailsContainer,
            children: [/*#__PURE__*/_jsxs("div", {
              className: styles.detailsLeftContainer,
              children: [/*#__PURE__*/_jsx(Controller, {
                control: control,
                name: "businessName",
                rules: companyNameValidation('Business name', {
                  required: 'Please enter your business name'
                }),
                render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                  collapseHelperText: false
                }, connectControllerToTextField(p)), {}, {
                  label: "Business name",
                  testId: testPrefix + '-business-name'
                }))
              }), BUSINESS_DESCRIPTION_ONBOARDING ? /*#__PURE__*/_jsx(Controller, {
                control: control,
                name: "businessDescription",
                render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                  collapseHelperText: true,
                  showHelperTextOnFocusOnly: true
                }, connectControllerToTextField(p)), {}, {
                  label: "Business description",
                  helperText: "Helps us customize your templates and content",
                  testId: BusinessDetailsTestIds.BusinessDescription()
                }))
              }) : undefined, isVendor && /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(Controller, {
                  control: control,
                  name: "address1",
                  rules: streetNameValidation(undefined, {
                    required: 'Please enter a street address'
                  }),
                  render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                    collapseHelperText: false
                  }, connectControllerToTextField(p)), {}, {
                    label: "Address 1",
                    testId: testPrefix + '-address1'
                  }))
                }), /*#__PURE__*/_jsx(Controller, {
                  control: control,
                  name: "address2",
                  rules: streetNameValidation(),
                  render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                    collapseHelperText: false
                  }, connectControllerToTextField(p)), {}, {
                    label: "Address 2",
                    testId: testPrefix + '-address2'
                  }))
                }), /*#__PURE__*/_jsxs("div", {
                  className: styles.address,
                  children: [/*#__PURE__*/_jsx(Controller, {
                    control: control,
                    name: "postalCode",
                    rules: postalCodeValidation(undefined, {
                      required: 'Please enter a postal code'
                    }),
                    render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                      collapseHelperText: false
                    }, connectControllerToTextField(p)), {}, {
                      label: "Postal code",
                      testId: testPrefix + '-postal-code'
                    }))
                  }), /*#__PURE__*/_jsx(Controller, {
                    control: control,
                    name: "city",
                    rules: cityNameValidation(undefined, {
                      required: 'Please enter a city'
                    }),
                    render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                      collapseHelperText: false
                    }, connectControllerToTextField(p)), {}, {
                      label: "City",
                      testId: testPrefix + '-city'
                    }))
                  }), /*#__PURE__*/_jsx(Controller, {
                    control: control,
                    name: "state",
                    rules: {
                      required: true
                    },
                    render: p => /*#__PURE__*/_jsx(Select, _objectSpread(_objectSpread({
                      collapseHelperText: false,
                      label: "State",
                      menuMaxHeight: 300
                    }, connectControllerToTextField(p)), {}, {
                      testId: testPrefix + '-dropdown-state',
                      children: states.map(state => /*#__PURE__*/_jsx(MenuItem, {
                        value: state,
                        children: state
                      }, state))
                    }))
                  })]
                })]
              })]
            }), isVendor && /*#__PURE__*/_jsx("div", {
              className: styles.detailsRightContainer,
              children: /*#__PURE__*/_jsx(Controller, {
                control: control,
                name: "logo",
                render: img => {
                  var _img$field$value$asDa;
                  return /*#__PURE__*/_jsx(LogoPicker, {
                    isNewUI: true,
                    image: (_img$field$value$asDa = img.field.value.asDataURL) !== null && _img$field$value$asDa !== void 0 ? _img$field$value$asDa : '',
                    noteBreakLine: true,
                    onChange: img.field.onChange,
                    testId: testPrefix + '-logo'
                  });
                }
              })
            })]
          }), /*#__PURE__*/_jsx(Button, {
            intent: Intent.UpdateBusinessDetails,
            type: "submit",
            form: formId,
            disabled: !formState.isValid,
            loading: formState.isSubmitting,
            testId: testPrefix + '-btn-save-changes',
            children: "Save changes"
          })]
        }), personaKYBStatus && isVendor && /*#__PURE__*/_jsxs(Card, {
          className: styles.fieldsLayout,
          autoHeight: true,
          disableSeparator: true,
          padding: "m",
          title: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "h5",
              weight: "normal",
              testId: "tax-information-title",
              children: "Tax information"
            }), /*#__PURE__*/_jsxs(Typography, {
              variant: "paragraph1",
              children: ["This information is used by Anchor to issue your 1099-K. ", /*#__PURE__*/_jsx(ExternalLinkButton, {
                href: helpArticles.kFormIssuance2024(),
                testId: 'tax-information-read-more',
                underline: "none",
                openInNewTab: true,
                children: "Learn more"
              })]
            })]
          }),
          children: [/*#__PURE__*/_jsx(Divider, {
            margin: 0
          }), [KYBProgressStatusEnum.NotStarted, KYBProgressStatusEnum.DetailsNeeded].includes(personaKYBStatus) && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Typography, {
              children: "Tax information becomes available after successfully completing the KYB verification."
            }), /*#__PURE__*/_jsx(Button, {
              onClick: startKYC,
              testId: "complete-kyb-button",
              children: "Complete KYB"
            })]
          }), (KYBProgressStatusHelpers.isAwaitingDecision(personaKYBStatus) || personaKYBStatus === KYBProgressStatusEnum.Declined) && /*#__PURE__*/_jsxs("div", {
            "data-testid": "awaiting-kyb",
            children: [/*#__PURE__*/_jsx(Typography, {
              children: "Tax information is available after successfully completing the KYB verification."
            }), /*#__PURE__*/_jsx(Typography, {
              skin: "disabled",
              children: "*Verification process takes several minutes up to three business days."
            })]
          }), personaKYBStatus === KYBProgressStatusEnum.Approved && /*#__PURE__*/_jsx(RestfulWrapper, {
            fetch: () => api.vendor.getTaxInfo(worksAt.id),
            renderLoader: () => /*#__PURE__*/_jsx(Spinner, {
              centeredInBox: true,
              centerBoxWidth: "100%",
              testId: "tax-info-loader"
            }),
            renderChild: _ref6 => {
              let {
                data
              } = _ref6;
              return /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(TextField, {
                  collapseHelperText: false,
                  label: "Business legal name",
                  value: data.businessLegalName,
                  disabled: true,
                  testId: "business-legal-name"
                }), /*#__PURE__*/_jsx(TextField, {
                  collapseHelperText: false,
                  label: "Business registered address",
                  value: data.registeredBusinessAddress,
                  disabled: true,
                  testId: "registered-business-address"
                }), /*#__PURE__*/_jsxs("div", {
                  className: styles.taxIdContainer,
                  children: [/*#__PURE__*/_jsx(TextField, {
                    collapseHelperText: false,
                    style: {
                      flex: '3'
                    },
                    label: "Business Tax Identification Number",
                    value: data.businessTaxId,
                    disabled: true,
                    testId: "business-tax-id"
                  }), /*#__PURE__*/_jsx(TextField, {
                    collapseHelperText: false,
                    style: {
                      flex: '1'
                    },
                    label: "Type",
                    value: data.businessTaxIdType.toUpperCase(),
                    disabled: true,
                    testId: "business-tax-id-type"
                  })]
                }), /*#__PURE__*/_jsx(TextField, {
                  collapseHelperText: false,
                  label: "Merchant Category Code",
                  value: data.merchantCategoryCode,
                  disabled: true,
                  testId: "merchant-category-code"
                })]
              });
            }
          })]
        }), isVendor && /*#__PURE__*/_jsxs(Card, {
          className: styles.fieldsLayout,
          autoHeight: true,
          padding: "m",
          disableSeparator: true,
          title: 'Billing settings',
          children: [/*#__PURE__*/_jsx(Divider, {
            margin: 0
          }), /*#__PURE__*/_jsx(Checkbox, {
            checked: grouped,
            onChange: e => {
              setGrouped(e.currentTarget.checked);
            },
            testId: "group-invoice-line-items",
            children: "Group identical services into a single line item on invoices"
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.prefixContainer,
            children: [/*#__PURE__*/_jsx(Controller, {
              control: control,
              name: "invoicePrefix",
              rules: invoicePrefixValidation('Invoice prefix', {
                required: 'Please enter an invoice prefix',
                maxLimit: maxInvoicePrefixLength
              }),
              render: p => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                collapseHelperText: false
              }, connectControllerToTextField(p)), {}, {
                label: "Invoices prefix",
                testId: "invoice-prefix",
                width: "220px",
                onChange: e => {
                  p.field.onChange(e.target.value.toUpperCase());
                }
              }))
            }), invoicePrefix !== ((_worksAt$settings5 = worksAt.settings) === null || _worksAt$settings5 === void 0 ? void 0 : _worksAt$settings5.invoicePrefix) && /*#__PURE__*/_jsx(Callout, {
              intent: "info",
              testId: "prefix-changes-info-callout",
              className: styles.callout,
              children: /*#__PURE__*/_jsx(Typography, {
                children: "Updating the invoice prefix will not modify existing invoices; it will only apply to new invoices moving forward."
              })
            })]
          }), /*#__PURE__*/_jsx(Button, {
            testId: "save-billing-changes",
            onClick: handleSaveBillingSettings,
            children: "Save changes"
          })]
        }), isVendor && /*#__PURE__*/_jsxs(Card, {
          className: styles.fieldsLayout,
          autoHeight: true,
          padding: "m",
          disableSeparator: true,
          title: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Typography, {
              weight: "normal",
              variant: "h5",
              children: "Bank account information"
            }), /*#__PURE__*/_jsx(Description, {
              testId: "bank-account-details-title",
              linkTestId: "bank-account-details-read-more"
            })]
          }),
          testId: 'bank-account-details',
          children: [/*#__PURE__*/_jsx(Divider, {
            margin: 0
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.bankDetails,
            children: [isBankAccountConnected(payoutBankAccount) ? /*#__PURE__*/_jsx(BankAccountDetails, {
              payoutBankAccount: payoutBankAccount
            }) : /*#__PURE__*/_jsx(Typography, {
              children: "No bank account connected."
            }), /*#__PURE__*/_jsx(ConnectButton, {
              className: styles.bankActions,
              enterManuallyTestId: "enter-manually",
              testId: "connect-bank-account"
            })]
          })]
        })]
      }), /*#__PURE__*/_jsx(NavigationGuardModal, {
        when: Object.keys(formState.dirtyFields).length > 0,
        primaryButtonTestId: "business-details-confirm-navigation",
        secondaryButtonTestId: "business-details-keep-editing"
      })]
    })
  });
}